import {
  ActionIcon,
  Button as MantineButton,
  Collapse,
  Loader,
  Menu,
  Select,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useEffect, useState } from 'react'

import { useMutation, useQuery } from '@redwoodjs/web'

import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  EXPIRE_JOB_ON_INDEED,
  INDEED_TRACKING_LINK_STATUS,
  SYNC_JOB_TO_INDEED,
} from 'src/graphql/indeedJobPostings.graphql'
import { GET_JOB_LISTING_TEMPLATE_QUESTIONS } from 'src/graphql/joblistings.graphql'
import { JOB_SOURCE_CONNECTIONS_QUERY } from 'src/graphql/jobSourceConnections.graphql'
import { GET_TRACKING_LINKS } from 'src/graphql/trackinglinks.graphql'
import { IndeedLogo as IndeedLogoAsset } from 'src/icons'
import IconAlertCircle from 'src/icons/IconAlertCircle'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconCancel from 'src/icons/IconCancel'
import IconCheckmarkCircle from 'src/icons/IconCheckmarkCircle'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconCircle from 'src/icons/IconCircle'
import IconCloseCircle from 'src/icons/IconCloseCircle'
import IconHourglass from 'src/icons/IconHourglass'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import IconRefresh from 'src/icons/IconRefresh'
import IconShareOpen from 'src/icons/IconShareOpen'
import IconThreeDots from 'src/icons/IconThreeDots'
import IconUpload from 'src/icons/IconUpload'

import TrackingLink from './TrackingLink'

type AssignmentOption = {
  employeeId: string
  label: string
  status?: string | null
}

type IndeedConnection = {
  jobSourceConnectionId: string
  type: string
  status: string
  jobSourceName?: string | null
  externalId?: string
}

type IndeedTrackingLinkProps = {
  link: {
    trackingLinkId: string
    linkType: string
    linkName?: string | null
    link: string
    externalPostingId?: string | null
    jobSourceConnectionId?: string | null
    leadEmployees?: { employeeId: string }[]
    memberEmployees?: { employeeId: string }[]
  }
  jobListingId: string
  handleDeleteModal: (linkId: string) => void
  showDeleteButton?: boolean
  isEditMode?: boolean
  onUpdateLinkName?: (trackingLinkId: string, linkName: string) => void
  allEmployeeOptions?: AssignmentOption[]
  jobDescription?: string | null
  jobEmploymentType?: string | null
  jobPayMin?: number | null
  jobPayUnit?: string | null
}

const IndeedLogo = () => (
  <img
    src={IndeedLogoAsset}
    alt="Indeed"
    className="h-4 w-auto shrink-0 object-contain pb-1"
  />
)

const iconClass = 'h-4 w-4 fill-none'

/**
 * Indeed can take up to a couple of hours to admit a brand-new posting into
 * its system, and until it does, a status lookup returns nothing at all —
 * indistinguishable from a posting that was deleted or that this account
 * cannot see. For this long after a sync we report the benign explanation
 * instead of alarming the recruiter about a posting that is simply still on
 * its way.
 */
const SYNC_GRACE_WINDOW_MS = 10 * 60 * 1000
const STATUS_POLL_INTERVAL_MS = 60 * 1000

const SYNCING_STATUS = {
  summaryLabel: 'Syncing to Indeed',
  summaryIcon: 'hourglass',
  description:
    'This job was just sent to Indeed. Indeed may take up to 2 hours to reflect the changes.',
  details: [{ label: 'Syncing to Indeed', icon: 'hourglass' }],
  imageUrl: null as string | null,
  imageAltText: null as string | null,
}

const StatusIcon = ({
  icon,
  colorClass,
}: {
  icon: string
  colorClass: string
}) => {
  const className = `${iconClass} ${colorClass}`
  switch (icon) {
    case 'hourglass':
      return <IconHourglass className={className} />
    case 'checkmarkCircle':
      return <IconCheckmarkCircle className={className} />
    case 'alertTriangle':
      return <IconAlertTriangle className={className} />
    case 'alertCircle':
      return <IconAlertCircle className={className} />
    case 'cancel':
      return <IconCancel className={className} />
    case 'closeCircle':
      return <IconCloseCircle className={className} />
    case 'circle':
      return <IconCircle className={className} />
    case 'infoCircle':
    default:
      return <IconInfoCircle className={className} />
  }
}

const statusStyles = (
  icon: string
): { text: string; stroke: string } => {
  switch (icon) {
    case 'checkmarkCircle':
      return {
        text: 'text-green-600',
        stroke: 'stroke-green-600',
      }
    case 'alertTriangle':
      return {
        text: 'text-amber-600',
        stroke: 'stroke-amber-500',
      }
    case 'alertCircle':
      return {
        text: 'text-amber-800',
        stroke: 'stroke-amber-800',
      }
    case 'cancel':
      return {
        text: 'text-red-600',
        stroke: 'stroke-red-600',
      }
    case 'hourglass':
    case 'closeCircle':
    case 'circle':
    case 'infoCircle':
    default:
      return {
        text: 'text-doubleNickel-gray-500',
        stroke: 'stroke-doubleNickel-gray-500',
      }
  }
}

const IndeedTrackingLink = ({
  link,
  jobListingId,
  handleDeleteModal,
  showDeleteButton = true,
  isEditMode = false,
  onUpdateLinkName,
  allEmployeeOptions = [],
  jobDescription,
  jobEmploymentType,
  jobPayMin,
  jobPayUnit,
}: IndeedTrackingLinkProps) => {
  const isSynced = Boolean(link.externalPostingId)
  const [expanded, { toggle: toggleExpanded }] = useDisclosure(!isSynced)
  const [expireOpened, { open: openExpire, close: closeExpire }] =
    useDisclosure(false)
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(
    link.jobSourceConnectionId ?? null
  )
  const [syncFailed, setSyncFailed] = useState(false)
  const [syncedAt, setSyncedAt] = useState<number | null>(null)

  // Clearing the timestamp when the window closes re-renders the row, so the
  // real status replaces "Syncing to Indeed" without the recruiter reopening it.
  useEffect(() => {
    if (syncedAt === null) return

    const remainingMs = syncedAt + SYNC_GRACE_WINDOW_MS - Date.now()
    if (remainingMs <= 0) {
      setSyncedAt(null)
      return
    }

    const timer = setTimeout(() => setSyncedAt(null), remainingMs)
    return () => clearTimeout(timer)
  }, [syncedAt])

  const withinSyncGraceWindow = syncedAt !== null

  const { data: connectionsData, loading: connectionsLoading } = useQuery(
    JOB_SOURCE_CONNECTIONS_QUERY,
    { fetchPolicy: 'cache-and-network' }
  )

  const { data: templateQuestionsData } = useQuery(
    GET_JOB_LISTING_TEMPLATE_QUESTIONS,
    {
      variables: { id: jobListingId },
      skip: !jobListingId,
      fetchPolicy: 'cache-and-network',
    }
  )

  const hasPublicTemplateQuestions = Boolean(
    templateQuestionsData?.jobListing?.jobListingTemplate?.questions?.some(
      (q: { visibility?: string }) => q.visibility === 'PUBLIC'
    )
  )

  const activeConnections: IndeedConnection[] =
    connectionsData?.jobSourceConnections?.items?.filter(
      (c: IndeedConnection) => c.status === 'ACTIVE' && c.type === 'INDEED'
    ) ?? []

  const hasConnections = activeConnections.length > 0
  const singleConnectionId =
    activeConnections.length === 1
      ? activeConnections[0].jobSourceConnectionId
      : null

  const {
    data: statusData,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useQuery(INDEED_TRACKING_LINK_STATUS, {
    variables: { trackingLinkId: link.trackingLinkId },
    skip: !isSynced || !hasConnections,
    fetchPolicy: 'network-only',
    // Bounded by the grace window, so a recruiter watching a fresh sync sees the
    // status land on its own.
    pollInterval: withinSyncGraceWindow ? STATUS_POLL_INTERVAL_MS : 0,
  })

  const [syncJob, { loading: syncing }] = useMutation(SYNC_JOB_TO_INDEED, {
    refetchQueries: [
      { query: GET_TRACKING_LINKS, variables: { jobListingId } },
      { query: JOB_SOURCE_CONNECTIONS_QUERY },
    ],
    onCompleted: async () => {
      setSyncFailed(false)
      setSyncedAt(Date.now())
      toast(
        'Synced. Indeed may take up to 2 hours to reflect the changes.',
        'success'
      )
      await refetchStatus()
    },
    onError: (error) => {
      setSyncFailed(true)
      toast(error.message || 'Unable to sync to Indeed', 'error')
    },
  })

  const [expireJob] = useMutation(EXPIRE_JOB_ON_INDEED, {
    refetchQueries: [
      { query: GET_TRACKING_LINKS, variables: { jobListingId } },
    ],
    onCompleted: () => {
      toast(
        'Expired. Indeed may take up to 2 hours to reflect the changes.',
        'success'
      )
    },
    onError: (error) => {
      toast(error.message || 'Unable to expire on Indeed', 'error')
    },
  })

  // Mirrors resolveConnectionId() below; the server refuses to post without
  // an approved sourceName, so surface it before the recruiter clicks Sync.
  const targetConnection = activeConnections.find(
    (c) =>
      c.jobSourceConnectionId ===
      (selectedConnectionId || link.jobSourceConnectionId || singleConnectionId)
  )

  const syncBlockedReasons = [
    targetConnection &&
      !targetConnection.jobSourceName?.trim() &&
      'Indeed source name needed for Job Sync. Ask a Double Nickel admin to set it in Settings → Job Sources.',
    !jobDescription?.trim() && 'Job description needed for Indeed Job Sync',
    !jobEmploymentType && 'Employment type needed for Indeed Job Sync',
    (!jobPayMin || !jobPayUnit) && 'Pay needed for Indeed Job Sync',
  ].filter((reason): reason is string => Boolean(reason))
  const syncBlockedReason = syncBlockedReasons[0] ?? null

  const liveStatus = statusData?.indeedTrackingLinkStatus

  // A posting Indeed has not ingested yet reads as POSTING_NOT_FOUND, which is
  // expected right after a sync — report it as in-progress rather than missing.
  const displayStatus =
    withinSyncGraceWindow && liveStatus?.statusReason === 'POSTING_NOT_FOUND'
      ? SYNCING_STATUS
      : liveStatus

  // Pre-sync: header shows icon only (labels live in the expanded body)
  const headerStatus = !isSynced
    ? {
        summaryLabel: null as string | null,
        summaryIcon: syncFailed ? 'alertTriangle' : 'circle',
      }
    : displayStatus
      ? {
          summaryLabel: displayStatus.summaryLabel as string,
          summaryIcon: displayStatus.summaryIcon as string,
        }
      : {
          summaryLabel: statusLoading ? null : 'Status unknown',
          summaryIcon: 'infoCircle',
        }

  const statusDescription = isSynced
    ? (displayStatus?.description as string | undefined) ??
      (statusLoading
        ? undefined
        : 'We could not read this posting’s status from Indeed.')
    : undefined

  // details[0] mirrors the summary shown in the header, so only the extra rows
  // (e.g. Sponsored) add information here.
  const extraDetails: { label: string; icon: string }[] =
    displayStatus?.details?.filter(
      (detail: { label: string }) => detail.label !== displayStatus.summaryLabel
    ) ?? []

  const styles = statusStyles(headerStatus.summaryIcon)

  const resolveConnectionId = () =>
    selectedConnectionId ||
    link.jobSourceConnectionId ||
    singleConnectionId ||
    undefined

  const handleSync = () => {
    if (syncBlockedReason) {
      toast(syncBlockedReason, 'error')
      return
    }
    const jobSourceConnectionId = resolveConnectionId()
    if (!isSynced && !jobSourceConnectionId) {
      toast('Select an Indeed account to sync', 'error')
      return
    }
    syncJob({
      variables: {
        trackingLinkId: link.trackingLinkId,
        jobSourceConnectionId: isSynced ? undefined : jobSourceConnectionId,
      },
    })
  }

  const viewUrl = liveStatus?.viewUrl

  const handleRowClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // The row's own controls (copy, delete, rename, assignments) must act
    // without also expanding or collapsing the Job Sync panel.
    const interactive = (event.target as HTMLElement).closest(
      'button, a, input, [data-row-toggle-ignore]'
    )
    if (interactive) return

    toggleExpanded()
  }

  // Without an ACTIVE Indeed integration, render as a plain tracking link
  if (connectionsLoading || !hasConnections) {
    return (
      <TrackingLink
        link={link}
        jobListingId={jobListingId}
        handleDeleteModal={handleDeleteModal}
        showDeleteButton={showDeleteButton}
        isEditMode={isEditMode}
        onUpdateLinkName={onUpdateLinkName}
        allEmployeeOptions={allEmployeeOptions}
      />
    )
  }

  return (
    <div className="flex flex-col">
      {/* Shares TrackingLink's markup so the row lines up with sibling links */}
      <div
        className="cursor-pointer rounded hover:bg-doubleNickel-gray-50"
        onClick={handleRowClick}
      >
        <TrackingLink
          link={link}
          jobListingId={jobListingId}
          handleDeleteModal={handleDeleteModal}
          showDeleteButton={showDeleteButton}
          isEditMode={isEditMode}
          onUpdateLinkName={onUpdateLinkName}
          allEmployeeOptions={allEmployeeOptions}
          titleSuffix={
            <ActionIcon
              variant="subtle"
              color="gray"
              size={16}
              onClick={toggleExpanded}
              aria-label={expanded ? 'Collapse Indeed sync' : 'Expand Indeed sync'}
              aria-expanded={expanded}
            >
              <IconChevronDown
                className={`h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500 transition-transform ${
                  expanded ? 'rotate-0' : '-rotate-90'
                }`}
              />
            </ActionIcon>
          }
        />
      </div>

      <Collapse in={expanded}>
        <div className="pb-1 pt-2">
          {/* Indeed status header */}
          <div className="mb-3 flex items-center gap-2">
            <IndeedLogo />
            <span className="text-sm font-medium text-doubleNickel-gray-900">
              Job Sync
            </span>
            <span className="flex-1" />
            {statusLoading && isSynced ? (
              <Loader size={14} color="gray" />
            ) : displayStatus?.imageUrl && isSynced ? (
              viewUrl ? (
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0"
                >
                  <img
                    src={displayStatus.imageUrl}
                    alt={
                      displayStatus.imageAltText ||
                      headerStatus.summaryLabel ||
                      'Indeed job status'
                    }
                    className="h-7 w-auto"
                  />
                </a>
              ) : (
                <img
                  src={displayStatus.imageUrl}
                  alt={
                    displayStatus.imageAltText ||
                    headerStatus.summaryLabel ||
                    'Indeed job status'
                  }
                  className="h-7 w-auto"
                />
              )
            ) : (
              <span className="flex items-center gap-1.5">
                {headerStatus.summaryLabel && (
                  <span className={`text-sm font-medium ${styles.text}`}>
                    {headerStatus.summaryLabel}
                  </span>
                )}
                <StatusIcon
                  icon={headerStatus.summaryIcon}
                  colorClass={styles.stroke}
                />
              </span>
            )}

            {isSynced && (
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Indeed actions"
                  >
                    <IconThreeDots className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={
                      <IconRefresh className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
                    }
                    onClick={handleSync}
                    disabled={syncing}
                  >
                    Re-sync
                  </Menu.Item>
                  {viewUrl && (
                    <Menu.Item
                      component="a"
                      href={viewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      leftSection={
                        <IconShareOpen className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
                      }
                    >
                      View on Indeed
                    </Menu.Item>
                  )}
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={
                      <IconCancel className="h-4 w-4 fill-none stroke-red-600" />
                    }
                    onClick={openExpire}
                  >
                    Expire on Indeed
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </div>

          {/* Before posting — dashed CTA panel */}
          {!isSynced && (
            <div className="rounded-md border border-dashed border-doubleNickel-gray-300 bg-doubleNickel-gray-50/40 p-3">
              <div className="text-sm font-semibold text-doubleNickel-gray-900">
                {syncFailed
                  ? 'Sync failed'
                  : "This job isn't on Indeed yet"}
              </div>
              <div className="mt-0.5 text-xs text-doubleNickel-gray-600">
                {syncFailed
                  ? 'Something went wrong syncing this job. Try again.'
                  : 'No posting is connected to this Indeed link.'}
              </div>
              {hasPublicTemplateQuestions && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-doubleNickel-gray-600">
                  <IconInfoCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 fill-none stroke-doubleNickel-gray-500" />
                  <span>
                    Public questions on this job&apos;s template will be posted
                    to Indeed as screener questions.
                  </span>
                </div>
              )}
              {activeConnections.length > 1 && (
                <div className="mt-3">
                  <Select
                    size="xs"
                    label="Indeed account"
                    placeholder="Select account"
                    data={activeConnections.map((c) => ({
                      value: c.jobSourceConnectionId,
                      label: c.jobSourceName || c.jobSourceConnectionId,
                    }))}
                    value={selectedConnectionId}
                    onChange={setSelectedConnectionId}
                  />
                </div>
              )}
              {syncBlockedReasons.map((reason) => (
                <div
                  key={reason}
                  className="mt-2 flex items-center gap-1.5 text-xs text-amber-700"
                >
                  <IconAlertTriangle className="h-3.5 w-3.5 fill-none stroke-amber-500" />
                  {reason}
                </div>
              ))}
              <div className="mt-3 flex justify-end">
                <Tooltip
                  label={syncBlockedReasons.join('. ') || 'Sync to Indeed'}
                  disabled={syncBlockedReasons.length === 0}
                >
                  <MantineButton
                    size="xs"
                    variant="default"
                    loading={syncing}
                    disabled={Boolean(syncBlockedReason) || syncing}
                    leftSection={
                      !syncing ? (
                        <IconUpload className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-800" />
                      ) : undefined
                    }
                    onClick={handleSync}
                  >
                    Sync to Indeed
                  </MantineButton>
                </Tooltip>
              </div>
            </div>
          )}

          {/* Posted — what the status means and what to do about it */}
          {isSynced && statusDescription && (
            <div className="rounded-md border border-doubleNickel-gray-200 bg-doubleNickel-gray-50/40 p-3">
              {extraDetails.length > 0 && (
                <div className="mb-2 flex flex-col gap-1.5">
                  {extraDetails.map((detail) => {
                    const detailStyles = statusStyles(detail.icon)
                    return (
                      <div
                        key={detail.label}
                        className={`flex items-center gap-1.5 text-xs font-medium ${detailStyles.text}`}
                      >
                        <StatusIcon
                          icon={detail.icon}
                          colorClass={detailStyles.stroke}
                        />
                        {detail.label}
                      </div>
                    )
                  })}
                </div>
              )}
              <div className="text-xs text-doubleNickel-gray-600">
                {statusDescription}
              </div>
            </div>
          )}
        </div>
      </Collapse>

      <DeleteModal
        opened={expireOpened}
        close={closeExpire}
        onDelete={async () => {
          await expireJob({
            variables: { trackingLinkId: link.trackingLinkId },
          })
        }}
        title="Expire on Indeed"
        description="Expire this job on Indeed? The Double Nickel job stays open; the Indeed posting will be closed."
      />
    </div>
  )
}

export default IndeedTrackingLink
